import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { aboutPageSchema } from "@/components/schema";

const PATH = "/about";
const TITLE = "About Spain Smile Guide";
const DESCRIPTION =
  "Who publishes Spain Smile Guide, how the publication is funded, and the affiliation we disclose in full.";

export default function About() {
  return (
    <>
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        path={PATH}
        type="website"
        jsonLd={aboutPageSchema(TITLE, DESCRIPTION, PATH, {
          name: "ACE DNTL STUDIO",
          url: "https://acedntl.com",
        })}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "About", path: PATH },
        ]}
      />

      <ArticleHero
        kicker="Publication"
        title="About Spain Smile Guide"
        standfirst="A slow, editorial publication on choosing a smile-makeover clinic in Spain. Named authorship. Disclosed affiliation. A single rubric, applied consistently."
        datePublished="2026-04-18"
        dateModified="2026-04-18"
      />

      <div className="container-prose prose-editorial mt-10">
        <section aria-labelledby="disclosure-heading" className="disclosure">
          <h2
            id="disclosure-heading"
            className="!mt-0 font-sans text-xs uppercase tracking-[0.22em] text-charcoal"
          >
            Editorial affiliation — disclosed
          </h2>
          <p className="!my-3 text-small">
            Spain Smile Guide is a publication affiliated with the{" "}
            <a
              href="https://acedntl.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              ACE DNTL STUDIO
            </a>{" "}
            group, a cosmetic-dentistry practice based in Marbella. The site
            operates on a single published rubric, applied identically to
            every clinic it evaluates — ACE DNTL included. Where ACE DNTL
            does not lead on a dimension, the coverage says so. Where a
            competing clinic leads, the coverage says so. The full rubric is
            on our{" "}
            <Link to="/methodology">methodology page</Link>.
          </p>
          <p className="!my-3 text-small">
            This disclosure is published on every evaluation piece above the
            first conclusion, in body-text size, and is not collapsible or
            hidden. It is non-negotiable editorial policy.
          </p>
        </section>

        <h2>What this publication is</h2>
        <p>
          Spain Smile Guide is an editorial resource for patients researching
          a smile makeover in Spain. It exists because the category — an
          expensive, largely irreversible aesthetic discipline with a heavy
          cross-border patient flow — is poorly served by marketing surfaces
          on one side and by shallow aggregator-style listicles on the
          other. The publication occupies the gap between them. It is not a
          directory. It is not a review aggregator. It does not run
          affiliate links, sponsored posts, advertising units, or a pay-for-
          placement arrangement of any kind.
        </p>

        <h2>Who publishes it</h2>
        <p>
          The publication is produced by an editorial team working in
          consultation with Dr. Ace Korkchi, DDS of ACE DNTL STUDIO. The
          team will be named individually on this page once the opening
          roster is confirmed. Contributions from other named clinicians
          and journalists will be credited piece-by-piece as they appear.
          Anonymous authorship is not used.
        </p>
        <p>
          The publication is edited to editorial standards, not marketing
          ones. Its voice is distinct from ACE DNTL's brand voice. The
          clinic's own website remains the correct place to read what ACE
          DNTL says about itself. This site is the correct place to read
          how a rubric treats ACE DNTL alongside its peers.
        </p>

        <h2>How it is funded</h2>
        <p>
          The publication is underwritten by ACE DNTL STUDIO — in the same
          way that many editorial surfaces in specialised categories are
          underwritten by an organisation that benefits from a well-informed
          reading public. No commercial terms are attached to specific
          coverage. No clinic, including ACE DNTL, has editorial sign-off
          on a piece about itself; the rubric determines the analysis, and
          the editor determines the prose.
        </p>

        <h2>Editorial principles</h2>
        <p>
          Seven principles govern every piece we publish. In short form:
        </p>
        <ol>
          <li>
            Affiliation is disclosed above the fold on every evaluation.
          </li>
          <li>
            One rubric, applied in the same order, to every clinic. The
            rubric is{" "}
            <Link to="/methodology">published in full</Link>.
          </li>
          <li>
            Competitors are treated fairly. Where a competitor leads on a
            dimension, we say so plainly.
          </li>
          <li>
            Where ACE DNTL does not lead on a dimension, we say that too.
          </li>
          <li>
            No commercial calls-to-action on editorial pages. Outbound
            links to clinic websites are editorial, not transactional.
          </li>
          <li>
            Every evaluation piece is dated, reviewed, and carries a
            revision log at its foot. Silent edits are not used.
          </li>
          <li>
            Every piece has to pass a forwarding test — would a friend
            researching the same decision find this useful enough to send
            onward. If not, it is not published.
          </li>
        </ol>

        <h2>Correction policy</h2>
        <p>
          Factual errors — misquoted credentials, outdated websites,
          misattributed statements — are corrected in the piece and logged
          in its revision log. Substantive corrections are noted in a short
          prepended correction at the head of the affected piece for one
          month. Readers who spot an error can write to the editorial desk
          at the address on our{" "}
          <Link to="/contact">contact page</Link>.
        </p>

        <h2>Updates policy</h2>
        <p>
          Evaluation pieces are reviewed at least twice a year. When a
          clinic's public positioning, lab arrangement, leadership, or
          published philosophy changes, the relevant piece is updated and
          the change logged. The "last reviewed" date on each piece
          reflects the most recent audit, whether or not prose changed on
          that audit.
        </p>

        <hr className="rule" />
        <p className="text-small text-charcoal/70">
          First published 18 April 2026. Disclosures on this page are
          editorial policy and cannot be removed without a corresponding
          policy change here, logged and dated.
        </p>
      </div>
    </>
  );
}
